// skipcq: JS-W1028, JS-0128
import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  doc,
  updateDoc,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db, functions } from '../utils/firebase';
import { onSnapshotWithPermissionRetry } from '../utils/onSnapshotWithPermissionRetry';
import { CALLABLE_BEER_STYLE_CREATE } from '../utils/callableBeerConstants';

import { toStyleNameKey } from '../utils/beerStyle/styleNameKey';
import { styleNeedsTypeMapping } from '../utils/beerStyle/styleNeedsTypeMapping';

/**
 * useBeerStyles
 * Fetches all documents from the `beerStyles` collection.
 */
function useBeerStyles() {
  const [beerStyles, setBeerStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const beerStylesRef = collection(db, 'beerStyles');
      const beerStylesQuery = query(beerStylesRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(beerStylesQuery);
      const rows = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setBeerStyles(rows);
      return rows;
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to load beer styles');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const beerStylesRef = collection(db, 'beerStyles');
    const beerStylesQuery = query(beerStylesRef, orderBy('name', 'asc'));

    const unsubscribe = onSnapshotWithPermissionRetry(
      onSnapshot,
      beerStylesQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }));
        setBeerStyles(rows);
        setLoading(false);
      },
      (listenError) => {
        Sentry.captureException(listenError);
        setError('Failed to load beer styles');
        setBeerStyles([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  /**
   * Update a beer style document and optimistically update local state.
   */
  const updateBeerStyle = useCallback(async (id, updates) => {
    try {
      setError(null);
      const ref = doc(db, 'beerStyles', id);
      const nextUpdates = { ...updates };
      if (Object.prototype.hasOwnProperty.call(nextUpdates, 'type')
        || Object.prototype.hasOwnProperty.call(nextUpdates, 'typeId')) {
        const stillNeedsMapping = styleNeedsTypeMapping({
          type: nextUpdates.type,
          typeId: nextUpdates.typeId,
          needsTypeMapping: nextUpdates.needsTypeMapping,
        });
        nextUpdates.needsTypeMapping = stillNeedsMapping;
      }
      await updateDoc(ref, nextUpdates);

      // Optimistically update local state and keep list sorted by name
      setBeerStyles((prev) => {
        const updated = prev.map((style) =>
          style.id === id ? { ...style, ...nextUpdates } : style,
        );
        return updated.sort((left, right) => {
          const nameA = (left.name || '').toLowerCase();
          const nameB = (right.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to update beer style');
      throw err;
    }
  }, []);

  /**
   * Create or reuse a beer style via the server-assigned catalog callable.
   * @returns {Promise<{id: string, name: string, type: string|null, typeId?: string, nameKey?: string}>}
   */
  const createBeerStyle = useCallback(async (name, type, typeId = null) => {
    try {
      const trimmedName = (name || '').trim();
      if (!trimmedName) {
        throw new Error('Invalid style name');
      }

      const createFn = httpsCallable(functions, CALLABLE_BEER_STYLE_CREATE);
      const result = await createFn({
        name: trimmedName,
        type: type || undefined,
        typeId: typeId || undefined,
      });
      const createdStyle = result?.data?.style;
      if (!createdStyle?.id) {
        throw new Error('Style create did not return a catalog id');
      }

      const newStyle = {
        id: createdStyle.id,
        name: createdStyle.name || trimmedName,
        type: createdStyle.type || type || null,
        typeId: createdStyle.typeId || typeId || null,
        nameKey: createdStyle.nameKey || toStyleNameKey(trimmedName),
      };
      setBeerStyles((prev) => {
        if (prev.some((style) => style.id === newStyle.id)) {
          return prev;
        }
        const updated = [...prev, newStyle];
        return updated.sort((left, right) => {
          const nameA = (left.name || '').toLowerCase();
          const nameB = (right.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });

      return newStyle;
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to create beer style');
      throw err;
    }
  }, []);

  return { beerStyles, loading, error, refresh, updateBeerStyle, createBeerStyle };
}

export default useBeerStyles;
export { sanitizeStyleId } from '../utils/beerStyle/sanitizeStyleId';

